import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, EmptyState, Chip, StatCard, TableLoadingSkeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, Eye, PencilSimpleLine, Gear, Calendar, CheckSquare, Warning, X, Coins } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const inrFull = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

const CYCLE_LABEL = {
    monthly: "Monthly",
    quarterly: "Quarterly",
    half_yearly: "Half-yearly",
    annual: "Annual",
};

const STATUS_VARIANT = {
    draft: "neutral",
    ready: "warn",
    generated: "success",
    closed: "default",
};

const emptyConfig = () => ({
    name: "",
    billing_cycle: "monthly",
    calculation: { type: "fixed", fixed_amount: "", sqft_rate: "", custom_amounts: {} },
    additional_charges: [],
    late_payment: { enabled: false, grace_days: 5, penalty_type: "fixed", penalty_amount: 0 },
    applicability: { type: "all", blocks: [], flat_ids: [] },
    active: true,
});

export default function Maintenance() {
    const [configs, setConfigs] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [flats, setFlats] = useState([]);
    const [loading, setLoading] = useState(true);

    // Dialogs
    const [configDlg, setConfigDlg] = useState({ open: false, editingId: null, form: emptyConfig() });
    const [periodDlg, setPeriodDlg] = useState({ open: false, configId: "", period_label: "", period_start: "", period_end: "", billing_date: "", due_date: "" });
    const [previewDlg, setPreviewDlg] = useState({ open: false, loading: false, data: null, meta: null });

    async function load() {
        setLoading(true);
        try {
            const [cfgs, prds, fls] = await Promise.all([
                api.get("/maintenance/configs"),
                api.get("/maintenance/billing-periods"),
                api.get("/flats"),
            ]);
            setConfigs(cfgs.data);
            setPeriods(prds.data);
            setFlats(fls.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const configById = useMemo(() => Object.fromEntries(configs.map(c => [c.id, c])), [configs]);
    const blocks = useMemo(() => Array.from(new Set(flats.map(f => f.block))).sort(), [flats]);

    // ---------------------- CONFIG SAVE ----------------------
    function openCreateConfig() {
        setConfigDlg({ open: true, editingId: null, form: emptyConfig() });
    }
    function openEditConfig(c) {
        setConfigDlg({
            open: true,
            editingId: c.id,
            form: {
                name: c.name,
                billing_cycle: c.billing_cycle,
                calculation: {
                    type: c.calculation?.type || "fixed",
                    fixed_amount: c.calculation?.fixed_amount ?? "",
                    sqft_rate: c.calculation?.sqft_rate ?? "",
                    custom_amounts: c.calculation?.custom_amounts || {},
                },
                additional_charges: (c.additional_charges || []).map(a => ({ ...a })),
                late_payment: { ...(c.late_payment || emptyConfig().late_payment) },
                applicability: {
                    type: c.applicability?.type || "all",
                    blocks: c.applicability?.blocks || [],
                    flat_ids: c.applicability?.flat_ids || [],
                },
                active: c.active !== false,
            },
        });
    }

    async function saveConfig() {
        const f = configDlg.form;
        if (!f.name.trim()) return toast.error("Name is required");
        // Build payload with numeric coercions.
        const calc = { type: f.calculation.type };
        if (calc.type === "fixed") {
            const v = parseFloat(f.calculation.fixed_amount);
            if (isNaN(v) || v < 0) return toast.error("Fixed amount must be a non-negative number");
            calc.fixed_amount = v;
        } else if (calc.type === "per_sqft") {
            const v = parseFloat(f.calculation.sqft_rate);
            if (isNaN(v) || v < 0) return toast.error("Sq.ft rate must be a non-negative number");
            calc.sqft_rate = v;
        } else if (calc.type === "custom") {
            calc.custom_amounts = Object.fromEntries(
                Object.entries(f.calculation.custom_amounts || {})
                    .filter(([, v]) => v !== "" && v !== null && !isNaN(parseFloat(v)))
                    .map(([k, v]) => [k, parseFloat(v)])
            );
        }
        for (const ac of f.additional_charges) {
            if (!ac.name?.trim()) return toast.error("Every additional charge needs a name");
            if (isNaN(parseFloat(ac.amount)) || parseFloat(ac.amount) < 0) return toast.error(`Amount for '${ac.name}' must be zero or positive`);
        }
        const payload = {
            name: f.name.trim(),
            billing_cycle: f.billing_cycle,
            calculation: calc,
            additional_charges: f.additional_charges.map(a => ({ name: a.name.trim(), description: a.description || null, amount: parseFloat(a.amount), active: a.active !== false })),
            late_payment: {
                enabled: !!f.late_payment.enabled,
                grace_days: parseInt(f.late_payment.grace_days || 0, 10),
                penalty_type: f.late_payment.penalty_type,
                penalty_amount: parseFloat(f.late_payment.penalty_amount || 0),
            },
            applicability: f.applicability,
            active: f.active,
        };
        try {
            if (configDlg.editingId) {
                await api.patch(`/maintenance/configs/${configDlg.editingId}`, payload);
                toast.success("Configuration updated");
            } else {
                await api.post("/maintenance/configs", payload);
                toast.success("Configuration created");
            }
            setConfigDlg({ open: false, editingId: null, form: emptyConfig() });
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function deleteConfig(c) {
        if (!window.confirm(`Delete configuration '${c.name}'? If it has billing periods, it will be archived (not deleted) to preserve history.`)) return;
        try {
            await api.delete(`/maintenance/configs/${c.id}`);
            toast.success("Configuration removed");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    // ---------------------- BILLING PERIOD ----------------------
    function openCreatePeriod(cid) {
        setPeriodDlg({ open: true, configId: cid || "", period_label: "", period_start: "", period_end: "", billing_date: "", due_date: "" });
    }
    async function submitPeriod() {
        const p = periodDlg;
        if (!p.configId) return toast.error("Pick a configuration");
        if (!p.period_label.trim()) return toast.error("Period label is required (e.g. 'August 2026')");
        if (!p.billing_date || !p.due_date) return toast.error("Billing and due dates are required");
        if (!p.period_start || !p.period_end) return toast.error("Period start and end dates are required");
        try {
            await api.post("/maintenance/billing-periods", {
                config_id: p.configId,
                period_label: p.period_label.trim(),
                period_start: p.period_start,
                period_end: p.period_end,
                billing_date: p.billing_date,
                due_date: p.due_date,
            });
            toast.success("Billing period created");
            setPeriodDlg({ ...periodDlg, open: false });
            load();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function deletePeriod(p) {
        if (!window.confirm(`Delete billing period '${p.period_label}'?`)) return;
        try { await api.delete(`/maintenance/billing-periods/${p.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatError(e)); }
    }

    async function openPreviewForPeriod(p) {
        setPreviewDlg({ open: true, loading: true, data: null, meta: { kind: "period", period: p } });
        try {
            const res = await api.get(`/maintenance/billing-periods/${p.id}/preview`);
            setPreviewDlg({ open: true, loading: false, data: res.data, meta: { kind: "period", period: p } });
        } catch (e) {
            toast.error(formatError(e));
            setPreviewDlg({ open: false, loading: false, data: null, meta: null });
        }
    }
    async function openPreviewForConfig(c) {
        setPreviewDlg({ open: true, loading: true, data: null, meta: { kind: "config", config: c } });
        try {
            const res = await api.post(`/maintenance/configs/${c.id}/preview`, {});
            setPreviewDlg({ open: true, loading: false, data: res.data, meta: { kind: "config", config: c } });
        } catch (e) {
            toast.error(formatError(e));
            setPreviewDlg({ open: false, loading: false, data: null, meta: null });
        }
    }

    async function generateInvoices(p) {
        if (!window.confirm(`Generate ${previewDlg.data?.flats_count || ""} invoices for '${p.period_label}'? This action cannot be undone.`)) return;
        try {
            const res = await api.post(`/maintenance/billing-periods/${p.id}/generate`);
            toast.success(`${res.data.invoice_count} invoices generated`);
            setPreviewDlg({ open: false, loading: false, data: null, meta: null });
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    // ---------------------- RENDER ----------------------
    const activeConfigs = configs.filter(c => c.active !== false && !c.archived);
    const draftPeriods = periods.filter(p => p.status === "draft");
    const generatedPeriods = periods.filter(p => p.status === "generated" || p.status === "closed");

    return (
        <div data-testid="maintenance-page">
            <PageHeader
                overline="Finance"
                title="Maintenance"
                description="Configure recurring maintenance charges, create billing periods, review calculations, then hand off to Invoices."
                actions={
                    <>
                        <Button variant="outline" onClick={() => openCreatePeriod("")} data-testid="btn-new-period" className="rounded-full">
                            <Calendar size={16} className="mr-1.5" /> New billing period
                        </Button>
                        <Button onClick={openCreateConfig} data-testid="btn-new-config" className="rounded-full">
                            <Plus size={16} className="mr-1.5" /> New configuration
                        </Button>
                    </>
                }
            />

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <StatCard label="Active configs" value={activeConfigs.length} hint={configs.length ? `${configs.length} total` : "None yet"} />
                <StatCard label="Draft periods" value={draftPeriods.length} hint="Not yet generated" />
                <StatCard label="Generated periods" value={generatedPeriods.length} hint="Invoices dispatched" accent />
                <StatCard label="Flats on file" value={flats.length} hint="Available for billing" />
            </div>

            {loading ? <TableLoadingSkeleton rows={4} /> : (
                <>
                    <SectionTitle title="Billing periods" hint="Draft → Preview → Generate invoices" />
                    {periods.length === 0 ? (
                        <EmptyState
                            title="No billing periods yet"
                            description="Create a maintenance configuration first, then define the billing period you want to invoice for."
                            action={<Button className="rounded-full" onClick={() => openCreatePeriod("")} data-testid="empty-new-period"><Calendar size={16} className="mr-1.5" /> Create billing period</Button>}
                        />
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-10">
                            {periods.map(p => {
                                const cfg = configById[p.config_id];
                                return (
                                    <div key={p.id} data-testid={`period-card-${p.id}`} className="bg-white border border-brand-line rounded-sm p-5">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{p.config_name || cfg?.name || "Configuration"}</p>
                                                <h3 className="font-heading text-xl text-brand-ink truncate">{p.period_label}</h3>
                                                <p className="text-xs text-brand-inkSoft mt-1">Billed {p.billing_date} · Due {p.due_date}</p>
                                            </div>
                                            <Chip variant={STATUS_VARIANT[p.status] || "neutral"}>{p.status}</Chip>
                                        </div>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            <Button size="sm" variant="outline" onClick={() => openPreviewForPeriod(p)} data-testid={`btn-preview-${p.id}`} className="rounded-full">
                                                <Eye size={14} className="mr-1" /> Review charges
                                            </Button>
                                            {p.status !== "generated" && p.status !== "closed" && (
                                                <Button size="sm" onClick={() => openPreviewForPeriod(p)} data-testid={`btn-generate-${p.id}`} className="rounded-full">
                                                    <CheckSquare size={14} className="mr-1" /> Generate invoices
                                                </Button>
                                            )}
                                            {p.status !== "generated" && p.status !== "closed" && (
                                                <button onClick={() => deletePeriod(p)} data-testid={`btn-delete-period-${p.id}`} className="ml-auto p-2 text-brand-inkSoft hover:text-red-600" title="Delete">
                                                    <Trash size={14} />
                                                </button>
                                            )}
                                        </div>
                                        {p.invoice_ids?.length > 0 && (
                                            <p className="text-xs text-brand-inkSoft mt-3">{p.invoice_ids.length} invoices generated · {p.generated_at ? new Date(p.generated_at).toLocaleDateString() : ""}</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <SectionTitle title="Configurations" hint="Reusable templates — snapshotted into each period so historical bills stay accurate." />
                    {configs.length === 0 ? (
                        <EmptyState
                            title="No maintenance configurations yet"
                            description="Start by defining how much each flat should be charged, any additional charges, and late-payment rules."
                            action={<Button className="rounded-full" onClick={openCreateConfig} data-testid="empty-new-config"><Plus size={16} className="mr-1.5" /> Create configuration</Button>}
                        />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {configs.map(c => (
                                <ConfigCard
                                    key={c.id}
                                    cfg={c}
                                    onEdit={() => openEditConfig(c)}
                                    onDelete={() => deleteConfig(c)}
                                    onPreview={() => openPreviewForConfig(c)}
                                    onCreatePeriod={() => openCreatePeriod(c.id)}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            <ConfigDialog
                state={configDlg}
                setState={setConfigDlg}
                onSave={saveConfig}
                flats={flats}
                blocks={blocks}
            />
            <PeriodDialog
                state={periodDlg}
                setState={setPeriodDlg}
                onSave={submitPeriod}
                configs={activeConfigs}
            />
            <PreviewDialog
                state={previewDlg}
                onClose={() => setPreviewDlg({ open: false, loading: false, data: null, meta: null })}
                onGenerate={generateInvoices}
            />
        </div>
    );
}

// ---------------------- Sub-components ----------------------
function SectionTitle({ title, hint }) {
    return (
        <div className="mb-4">
            <h2 className="font-heading text-xl text-brand-ink">{title}</h2>
            {hint && <p className="text-xs text-brand-inkSoft mt-1">{hint}</p>}
        </div>
    );
}

function ConfigCard({ cfg, onEdit, onDelete, onPreview, onCreatePeriod }) {
    const calc = cfg.calculation || {};
    const rateText = calc.type === "fixed" ? `${inr(calc.fixed_amount)} / flat`
        : calc.type === "per_sqft" ? `${inrFull(calc.sqft_rate)} / sq.ft`
        : "Custom per flat";
    const addOns = (cfg.additional_charges || []).filter(a => a.active !== false);
    return (
        <div className="bg-white border border-brand-line rounded-sm p-5" data-testid={`config-card-${cfg.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{CYCLE_LABEL[cfg.billing_cycle] || cfg.billing_cycle}</p>
                    <h3 className="font-heading text-lg text-brand-ink truncate">{cfg.name}</h3>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={onEdit} data-testid={`btn-edit-config-${cfg.id}`} className="p-2 text-brand-inkSoft hover:text-brand-action" title="Edit"><PencilSimpleLine size={14} /></button>
                    <button onClick={onDelete} data-testid={`btn-delete-config-${cfg.id}`} className="p-2 text-brand-inkSoft hover:text-red-600" title="Delete"><Trash size={14} /></button>
                </div>
            </div>
            <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                    <span className="text-brand-inkSoft">Base rate</span>
                    <span className="text-brand-ink font-medium">{rateText}</span>
                </div>
                {addOns.length > 0 && (
                    <div>
                        <span className="text-brand-inkSoft text-xs">Additional charges</span>
                        <ul className="mt-1 text-xs text-brand-ink space-y-0.5">
                            {addOns.map((a, i) => (
                                <li key={i} className="flex justify-between"><span>{a.name}</span><span>{inr(a.amount)}</span></li>
                            ))}
                        </ul>
                    </div>
                )}
                {cfg.late_payment?.enabled && (
                    <div className="flex items-start gap-2 text-xs text-[#EA580C] bg-[#FFF7ED]/60 rounded-sm px-2 py-1.5 mt-2">
                        <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                        <span>Late fee: {cfg.late_payment.penalty_type === "percent" ? `${cfg.late_payment.penalty_amount}%` : inr(cfg.late_payment.penalty_amount)} after {cfg.late_payment.grace_days} days</span>
                    </div>
                )}
                <div className="text-xs text-brand-inkSoft mt-2">
                    Applicability: {cfg.applicability?.type === "all" ? "All flats"
                        : cfg.applicability?.type === "blocks" ? `Blocks ${(cfg.applicability.blocks || []).join(", ") || "—"}`
                        : `${(cfg.applicability?.flat_ids || []).length} selected flats`}
                </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={onPreview} data-testid={`btn-preview-config-${cfg.id}`} className="rounded-full">
                    <Eye size={14} className="mr-1" /> Preview
                </Button>
                <Button size="sm" onClick={onCreatePeriod} data-testid={`btn-newperiod-config-${cfg.id}`} className="rounded-full">
                    <Calendar size={14} className="mr-1" /> New period
                </Button>
            </div>
        </div>
    );
}

function ConfigDialog({ state, setState, onSave, flats, blocks }) {
    const { open, editingId, form } = state;
    const setForm = (updater) => setState(s => ({ ...s, form: typeof updater === "function" ? updater(s.form) : updater }));

    function addCharge() {
        setForm(f => ({ ...f, additional_charges: [...f.additional_charges, { name: "", description: "", amount: 0, active: true }] }));
    }
    function updateCharge(i, patch) {
        setForm(f => ({ ...f, additional_charges: f.additional_charges.map((a, idx) => idx === i ? { ...a, ...patch } : a) }));
    }
    function removeCharge(i) {
        setForm(f => ({ ...f, additional_charges: f.additional_charges.filter((_, idx) => idx !== i) }));
    }

    return (
        <Dialog open={open} onOpenChange={(v) => setState(s => ({ ...s, open: v }))}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="config-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">{editingId ? "Edit" : "New"} maintenance configuration</DialogTitle>
                    <DialogDescription>Set up how maintenance charges are calculated and applied. This becomes a reusable template.</DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-2">
                    {/* Basics */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <Label>Name</Label>
                            <Input data-testid="cfg-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Standard Monthly 2026" className="rounded-sm border-brand-line mt-1" />
                        </div>
                        <div>
                            <Label>Billing cycle</Label>
                            <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                                <SelectTrigger className="rounded-sm border-brand-line mt-1" data-testid="cfg-cycle"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="monthly">Monthly</SelectItem>
                                    <SelectItem value="quarterly">Quarterly</SelectItem>
                                    <SelectItem value="half_yearly">Half-yearly</SelectItem>
                                    <SelectItem value="annual">Annual</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Calculation */}
                    <FieldGroup title="Base charge calculation">
                        <Select value={form.calculation.type} onValueChange={(v) => setForm({ ...form, calculation: { ...form.calculation, type: v } })}>
                            <SelectTrigger className="rounded-sm border-brand-line" data-testid="cfg-calc-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="fixed">Fixed amount per flat</SelectItem>
                                <SelectItem value="per_sqft">Per square foot</SelectItem>
                                <SelectItem value="custom">Custom amount per flat</SelectItem>
                            </SelectContent>
                        </Select>
                        {form.calculation.type === "fixed" && (
                            <div className="mt-3">
                                <Label>Amount per flat (₹)</Label>
                                <Input type="number" min="0" step="0.01" data-testid="cfg-fixed-amount" value={form.calculation.fixed_amount} onChange={e => setForm({ ...form, calculation: { ...form.calculation, fixed_amount: e.target.value } })} className="rounded-sm border-brand-line mt-1" />
                            </div>
                        )}
                        {form.calculation.type === "per_sqft" && (
                            <div className="mt-3">
                                <Label>Rate per sq.ft (₹)</Label>
                                <Input type="number" min="0" step="0.01" data-testid="cfg-sqft-rate" value={form.calculation.sqft_rate} onChange={e => setForm({ ...form, calculation: { ...form.calculation, sqft_rate: e.target.value } })} className="rounded-sm border-brand-line mt-1" />
                                <p className="text-xs text-brand-inkSoft mt-2">Flats without an area on file will be flagged during preview.</p>
                            </div>
                        )}
                        {form.calculation.type === "custom" && (
                            <div className="mt-3">
                                <p className="text-xs text-brand-inkSoft mb-2">Set an amount per flat. Leave blank to skip a flat.</p>
                                <div className="max-h-64 overflow-y-auto border border-brand-line rounded-sm p-2 grid grid-cols-2 gap-2">
                                    {flats.map(f => (
                                        <div key={f.id} className="flex items-center gap-2">
                                            <span className="text-xs text-brand-ink w-16 shrink-0">{f.block}-{f.number}</span>
                                            <Input type="number" min="0" step="0.01"
                                                data-testid={`cfg-custom-${f.id}`}
                                                value={form.calculation.custom_amounts[f.id] ?? ""}
                                                onChange={e => setForm({
                                                    ...form,
                                                    calculation: {
                                                        ...form.calculation,
                                                        custom_amounts: { ...form.calculation.custom_amounts, [f.id]: e.target.value },
                                                    },
                                                })}
                                                className="rounded-sm border-brand-line h-8 text-sm"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </FieldGroup>

                    {/* Additional charges */}
                    <FieldGroup title="Additional charges" actions={
                        <Button size="sm" variant="outline" onClick={addCharge} data-testid="cfg-add-charge" className="rounded-full"><Plus size={14} className="mr-1" /> Add charge</Button>
                    }>
                        {form.additional_charges.length === 0 && <p className="text-xs text-brand-inkSoft">No additional charges. Add parking, water, sinking fund, etc.</p>}
                        <div className="space-y-2">
                            {form.additional_charges.map((a, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                    <Input placeholder="Name (e.g. Parking)" value={a.name} onChange={e => updateCharge(i, { name: e.target.value })} className="col-span-4 rounded-sm border-brand-line" data-testid={`cfg-ac-name-${i}`} />
                                    <Input placeholder="Description (optional)" value={a.description || ""} onChange={e => updateCharge(i, { description: e.target.value })} className="col-span-4 rounded-sm border-brand-line" />
                                    <Input type="number" min="0" step="0.01" placeholder="Amount" value={a.amount} onChange={e => updateCharge(i, { amount: e.target.value })} className="col-span-3 rounded-sm border-brand-line" data-testid={`cfg-ac-amount-${i}`} />
                                    <button onClick={() => removeCharge(i)} className="col-span-1 p-2 text-brand-inkSoft hover:text-red-600" title="Remove"><X size={14} /></button>
                                </div>
                            ))}
                        </div>
                    </FieldGroup>

                    {/* Late payment */}
                    <FieldGroup title="Late payment rules">
                        <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-2">
                                <span>Enable late-payment penalty</span>
                            </Label>
                            <Switch checked={!!form.late_payment.enabled} onCheckedChange={(v) => setForm({ ...form, late_payment: { ...form.late_payment, enabled: v } })} data-testid="cfg-late-enable" />
                        </div>
                        {form.late_payment.enabled && (
                            <div className="grid md:grid-cols-3 gap-3 mt-3">
                                <div>
                                    <Label>Grace days</Label>
                                    <Input type="number" min="0" max="365" value={form.late_payment.grace_days} onChange={e => setForm({ ...form, late_payment: { ...form.late_payment, grace_days: e.target.value } })} className="rounded-sm border-brand-line mt-1" />
                                </div>
                                <div>
                                    <Label>Penalty type</Label>
                                    <Select value={form.late_payment.penalty_type} onValueChange={(v) => setForm({ ...form, late_payment: { ...form.late_payment, penalty_type: v } })}>
                                        <SelectTrigger className="rounded-sm border-brand-line mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="fixed">Fixed amount (₹)</SelectItem>
                                            <SelectItem value="percent">Percent of invoice</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>{form.late_payment.penalty_type === "percent" ? "Percent (%)" : "Amount (₹)"}</Label>
                                    <Input type="number" min="0" step="0.01" value={form.late_payment.penalty_amount} onChange={e => setForm({ ...form, late_payment: { ...form.late_payment, penalty_amount: e.target.value } })} className="rounded-sm border-brand-line mt-1" />
                                </div>
                            </div>
                        )}
                        <p className="text-xs text-brand-inkSoft mt-2">Penalty is computed when an invoice becomes overdue — not on save.</p>
                    </FieldGroup>

                    {/* Applicability */}
                    <FieldGroup title="Applicability">
                        <Select value={form.applicability.type} onValueChange={(v) => setForm({ ...form, applicability: { ...form.applicability, type: v } })}>
                            <SelectTrigger className="rounded-sm border-brand-line" data-testid="cfg-appl-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All flats</SelectItem>
                                <SelectItem value="blocks">Specific blocks</SelectItem>
                                <SelectItem value="flats">Specific flats</SelectItem>
                            </SelectContent>
                        </Select>
                        {form.applicability.type === "blocks" && (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {blocks.map(b => {
                                    const on = form.applicability.blocks.includes(b);
                                    return (
                                        <button key={b} onClick={() => setForm({
                                            ...form,
                                            applicability: {
                                                ...form.applicability,
                                                blocks: on ? form.applicability.blocks.filter(x => x !== b) : [...form.applicability.blocks, b],
                                            }
                                        })} className={`text-xs px-3 py-1 rounded-full border ${on ? "bg-brand-action text-white border-brand-action" : "bg-white text-brand-ink border-brand-line"}`}>
                                            Block {b}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {form.applicability.type === "flats" && (
                            <div className="mt-3 max-h-48 overflow-y-auto border border-brand-line rounded-sm p-2 grid grid-cols-4 gap-1">
                                {flats.map(f => {
                                    const on = form.applicability.flat_ids.includes(f.id);
                                    return (
                                        <button key={f.id} onClick={() => setForm({
                                            ...form,
                                            applicability: {
                                                ...form.applicability,
                                                flat_ids: on ? form.applicability.flat_ids.filter(x => x !== f.id) : [...form.applicability.flat_ids, f.id],
                                            }
                                        })} className={`text-xs px-2 py-1 rounded-sm ${on ? "bg-brand-action text-white" : "bg-white text-brand-ink border border-brand-line"}`}>
                                            {f.block}-{f.number}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </FieldGroup>
                </div>

                <DialogFooter>
                    <Button variant="outline" className="rounded-full" onClick={() => setState(s => ({ ...s, open: false }))}>Cancel</Button>
                    <Button className="rounded-full" onClick={onSave} data-testid="cfg-save">Save configuration</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PeriodDialog({ state, setState, onSave, configs }) {
    return (
        <Dialog open={state.open} onOpenChange={(v) => setState(s => ({ ...s, open: v }))}>
            <DialogContent className="max-w-lg" data-testid="period-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">New billing period</DialogTitle>
                    <DialogDescription>Snapshot the chosen configuration into a period. You can review before generating invoices.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Configuration</Label>
                        <Select value={state.configId} onValueChange={(v) => setState(s => ({ ...s, configId: v }))}>
                            <SelectTrigger className="rounded-sm border-brand-line mt-1" data-testid="period-config"><SelectValue placeholder="Pick a configuration" /></SelectTrigger>
                            <SelectContent>
                                {configs.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {CYCLE_LABEL[c.billing_cycle]}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Period label</Label>
                        <Input placeholder="e.g. August 2026" value={state.period_label} onChange={e => setState(s => ({ ...s, period_label: e.target.value }))} data-testid="period-label" className="rounded-sm border-brand-line mt-1" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Period start</Label>
                            <Input type="date" value={state.period_start} onChange={e => setState(s => ({ ...s, period_start: e.target.value }))} data-testid="period-start" className="rounded-sm border-brand-line mt-1" />
                        </div>
                        <div>
                            <Label>Period end</Label>
                            <Input type="date" value={state.period_end} onChange={e => setState(s => ({ ...s, period_end: e.target.value }))} data-testid="period-end" className="rounded-sm border-brand-line mt-1" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Billing date</Label>
                            <Input type="date" value={state.billing_date} onChange={e => setState(s => ({ ...s, billing_date: e.target.value }))} data-testid="period-billing" className="rounded-sm border-brand-line mt-1" />
                        </div>
                        <div>
                            <Label>Due date</Label>
                            <Input type="date" value={state.due_date} onChange={e => setState(s => ({ ...s, due_date: e.target.value }))} data-testid="period-due" className="rounded-sm border-brand-line mt-1" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" className="rounded-full" onClick={() => setState(s => ({ ...s, open: false }))}>Cancel</Button>
                    <Button className="rounded-full" onClick={onSave} data-testid="period-save">Create period</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PreviewDialog({ state, onClose, onGenerate }) {
    const { open, loading, data, meta } = state;
    const isPeriod = meta?.kind === "period";
    const canGenerate = isPeriod && data && data.missing_count === 0 && meta?.period?.status !== "generated" && meta?.period?.status !== "closed";
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="preview-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">
                        {isPeriod ? `Preview — ${meta.period.period_label}` : `Preview — ${meta?.config?.name || "Configuration"}`}
                    </DialogTitle>
                    <DialogDescription>{isPeriod ? "Review computed charges before invoices are generated." : "Estimated charges if you were to bill this configuration today."}</DialogDescription>
                </DialogHeader>

                {loading && <p className="py-8 text-center text-brand-inkSoft">Calculating…</p>}
                {!loading && data && (
                    <>
                        <div className="grid md:grid-cols-3 gap-3 mb-4">
                            <StatCard label="Applicable flats" value={data.flats_count} />
                            <StatCard label="Estimated total" value={inrFull(data.estimated_total)} accent />
                            <StatCard label="Missing info" value={data.missing_count} hint={data.missing_count ? "Fix these before generating" : "All good"} />
                        </div>
                        <div className="max-h-[50vh] overflow-y-auto border border-brand-line rounded-sm">
                            <table className="w-full text-sm">
                                <thead className="bg-brand-bg sticky top-0">
                                    <tr className="text-[10px] uppercase tracking-overline text-brand-inkSoft">
                                        <th className="px-3 py-2 text-left">Flat</th>
                                        <th className="px-3 py-2 text-left">Resident</th>
                                        <th className="px-3 py-2 text-right">Base</th>
                                        <th className="px-3 py-2 text-left">Additional</th>
                                        <th className="px-3 py-2 text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.rows.map(r => (
                                        <tr key={r.flat_id} className={`border-t border-brand-line ${r.missing ? "bg-[#FFF7ED]/60" : ""}`} data-testid={`preview-row-${r.flat_id}`}>
                                            <td className="px-3 py-2 font-medium text-brand-ink">{r.flat_label}</td>
                                            <td className="px-3 py-2 text-brand-inkSoft">{r.resident_name || "—"}</td>
                                            <td className="px-3 py-2 text-right">{inr(r.base_amount)}</td>
                                            <td className="px-3 py-2 text-xs text-brand-inkSoft">
                                                {r.additional.length ? r.additional.map(a => `${a.name} ${inr(a.amount)}`).join(" · ") : "—"}
                                            </td>
                                            <td className="px-3 py-2 text-right font-medium">
                                                {r.missing ? <span className="text-[#DC2626] text-xs">{r.missing}</span> : inrFull(r.total)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                <DialogFooter>
                    <Button variant="outline" className="rounded-full" onClick={onClose}>Close</Button>
                    {canGenerate && (
                        <Button className="rounded-full" onClick={() => onGenerate(meta.period)} data-testid="preview-generate">
                            <CheckSquare size={14} className="mr-1" /> Generate {data.flats_count} invoices
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldGroup({ title, actions, children }) {
    return (
        <div className="border border-brand-line rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
                <h4 className="font-heading text-sm uppercase tracking-overline text-brand-inkSoft">{title}</h4>
                {actions}
            </div>
            {children}
        </div>
    );
}
